import { z } from 'zod';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from './constants';

// ---------- 基础 ----------

export const IdSchema = z.string().min(1);

export const RectSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number().min(0),
  h: z.number().min(0),
  rotate: z.number().default(0),
});
export type Rect = z.infer<typeof RectSchema>;

export const AnchorSchema = z.enum(['top', 'center', 'bottom']);
export type Anchor = z.infer<typeof AnchorSchema>;

export const LayerStyleSchema = z.object({
  opacity: z.number().min(0).max(1).default(1),
  zIndex: z.number().int().default(0),
});
export type LayerStyle = z.infer<typeof LayerStyleSchema>;

// ---------- 背景 ----------

export const BackgroundSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('color'), color: z.string() }),
  z.object({
    type: z.literal('image'),
    src: z.string(),
    color: z.string().optional(),
    fit: z.enum(['cover', 'contain', 'fill']).default('cover'),
  }),
]);
export type Background = z.infer<typeof BackgroundSchema>;

// ---------- 动画 ----------

export const AnimationTriggerSchema = z.enum(['enter', 'click']);
export type AnimationTrigger = z.infer<typeof AnimationTriggerSchema>;

export const AnimationSchema = z.object({
  preset: z.string(),
  trigger: AnimationTriggerSchema.default('enter'),
  duration: z.number().min(0).default(600),
  delay: z.number().min(0).default(0),
  iterations: z.number().min(0).default(1), // 0 = infinite
  easing: z.string().default('ease-out'),
});
export type Animation = z.infer<typeof AnimationSchema>;

// ---------- 事件 ----------

export const ActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('gotoPage'), pageId: IdSchema }),
  z.object({ type: z.literal('nextPage') }),
  z.object({ type: z.literal('prevPage') }),
  z.object({ type: z.literal('openLink'), url: z.string(), newWindow: z.boolean().default(false) }),
  z.object({ type: z.literal('openPopup'), popupId: IdSchema }),
  z.object({ type: z.literal('closePopup') }),
  z.object({ type: z.literal('call'), phone: z.string() }),
  z.object({ type: z.literal('submitForm'), formId: IdSchema }),
  /** 全屏预览一张图片（H5DS 的"图片放大"），点击遮罩关闭 */
  z.object({ type: z.literal('previewImage'), src: z.string() }),
]);
export type Action = z.infer<typeof ActionSchema>;

export const EventBindingSchema = z.object({
  on: z.enum(['click']).default('click'),
  action: ActionSchema,
});
export type EventBinding = z.infer<typeof EventBindingSchema>;

// ---------- 图层 ----------

export const LayerSchema = z.object({
  id: IdSchema,
  type: z.string(),
  materialVersion: z.number().int().min(1).default(1),
  name: z.string().default(''),
  rect: RectSchema,
  anchor: AnchorSchema.default('top'),
  style: LayerStyleSchema.default({ opacity: 1, zIndex: 0 }),
  props: z.record(z.string(), z.unknown()).default({}),
  animations: z.array(AnimationSchema).default([]),
  events: z.array(EventBindingSchema).default([]),
  locked: z.boolean().default(false),
  hidden: z.boolean().default(false),
  /** 仅浮动层（Work.fixeds）使用：在这些页面上不显示，如"返回"按钮在首页隐藏 */
  excludePages: z.array(IdSchema).optional(),
});
export type Layer = z.infer<typeof LayerSchema>;

// ---------- 页面 ----------

export const PageModeSchema = z.enum(['fixed', 'long']);
export type PageMode = z.infer<typeof PageModeSchema>;

export const PageTransitionSchema = z.enum(['slide-up', 'fade', 'none']);
export type PageTransition = z.infer<typeof PageTransitionSchema>;

export const PageSchema = z.object({
  id: IdSchema,
  name: z.string().default(''),
  mode: PageModeSchema.default('fixed'),
  /** long 模式下的内容高度（设计像素）；fixed 模式忽略 */
  height: z.number().min(CANVAS_HEIGHT).default(CANVAS_HEIGHT),
  background: BackgroundSchema.default({ type: 'color', color: '#ffffff' }),
  transition: PageTransitionSchema.default('slide-up'),
  layers: z.array(LayerSchema).default([]),
});
export type Page = z.infer<typeof PageSchema>;

// ---------- 作品 ----------

export const ShareSchema = z.object({
  title: z.string().default(''),
  desc: z.string().default(''),
  image: z.string().default(''),
});
export type Share = z.infer<typeof ShareSchema>;

export const BgmSchema = z.object({
  src: z.string(),
  loop: z.boolean().default(true),
  autoplay: z.boolean().default(true),
});
export type Bgm = z.infer<typeof BgmSchema>;

export const GlobalsSchema = z.object({
  bgm: BgmSchema.optional(),
  fonts: z.array(z.string()).default([]),
  loading: z.enum(['default', 'none']).default('default'),
  share: ShareSchema.default({ title: '', desc: '', image: '' }),
});
export type Globals = z.infer<typeof GlobalsSchema>;

export const WorkSchema = z.object({
  schemaVersion: z.number().int().min(1),
  id: IdSchema,
  title: z.string().default('未命名作品'),
  cover: z.string().optional(),
  canvas: z.object({
    width: z.literal(CANVAS_WIDTH).default(CANVAS_WIDTH),
    height: z.literal(CANVAS_HEIGHT).default(CANVAS_HEIGHT),
  }).default({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }),
  globals: GlobalsSchema.default({ fonts: [], loading: 'default', share: { title: '', desc: '', image: '' } }),
  pages: z.array(PageSchema).min(1),
  popups: z.array(PageSchema).default([]),
  /** 浮动层：固定叠在所有页面之上、不随翻页与滚动移动（返回按钮、悬浮客服等）。坐标按 375×667 画布，anchor 决定纵向贴边 */
  fixeds: z.array(LayerSchema).default([]),
});
export type Work = z.infer<typeof WorkSchema>;
